// The bubble map's physics (the "bubble" layout): every topic is a circle,
// every branch line and relationship a spring, every pair of topics pushes
// apart, a weak pull keeps the map centred, and circles never overlap. Like
// Obsidian's graph view, but the graph is an editable mindmap.
//
// Positions are saved on each topic (gx, gy: the circle's centre) once the
// map settles, so a map opens exactly as it was left; `pin` holds a topic
// where it was dropped. Pure functions: the editor runs `step` live (the map
// moves), the file export and Node run `settle` (the same maths, all at once).

/** How strongly each force acts; a map's own `graph` settings scale them. */
export const FORCE_DEFAULTS = { repel: 1, distance: 1, link: 1, center: 1 };
export const graphOpts = (map) => ({ style: 'bubbles', ...FORCE_DEFAULTS, ...(map.graph || {}) });

/**
 * A starting place for every topic without a saved position: around its
 * parent, fanned away from the grandparent (the central topic's branches go
 * all the way round). Deterministic, so a new map always starts the same.
 */
export function placeNew(nodes, byId, parentOf, childrenOf, gap) {
  const place = (id, depth) => {
    const n = byId.get(id);
    const kids = childrenOf(id).map((k) => byId.get(k)).filter(Boolean);
    const p = parentOf(id) ? byId.get(parentOf(id)) : null;
    const base = p ? Math.atan2(n.y - p.y, n.x - p.x) : -Math.PI / 2;
    const spread = depth === 0 ? Math.PI * 2 : Math.min(Math.PI * 1.1, 0.5 + kids.length * 0.45);
    kids.forEach((k, i) => {
      if (k.placed) return;
      const t = depth === 0 ? base + (i / kids.length) * spread : base - spread / 2 + (kids.length === 1 ? spread / 2 : (i / (kids.length - 1)) * spread);
      const d = n.r + k.r + gap * (depth === 0 ? 1.4 : 1);
      k.x = n.x + Math.cos(t) * d; k.y = n.y + Math.sin(t) * d;
      k.placed = true;
    });
    for (const k of kids) place(k.id, depth + 1);
  };
  for (const n of nodes) if (n.placed && !parentOf(n.id)) place(n.id, 0);
  // anything still without a place (a floating topic's branch whose top was unplaced): near the origin
  let a = 0;
  for (const n of nodes) if (!n.placed) { n.x = Math.cos(a) * 200; n.y = Math.sin(a) * 200; a += 2.4; n.placed = true; place(n.id, 1); }
}

/**
 * One tick of the simulation. nodes: [{ id, x, y, vx, vy, r, fixed, ghost? }],
 * links: [{ a, b, len, k }] (indexes into nodes). Returns the largest move.
 */
export function step(nodes, links, alpha, f = FORCE_DEFAULTS) {
  const n = nodes.length;
  const repel = 1400 * f.repel;
  for (let i = 0; i < n; i++) {
    const p = nodes[i];
    if (p.ghost) continue;
    for (let j = i + 1; j < n; j++) {
      const q = nodes[j];
      if (q.ghost) continue; // a bubble being dragged passes over the others, so it can be dropped on one
      let dx = q.x - p.x; let dy = q.y - p.y;
      let d2 = dx * dx + dy * dy;
      if (d2 > 1.44e6) continue; // beyond 1200: no push
      if (d2 < 1) { dx = (i - j) * 0.7 + 0.3; dy = (j - i) * 0.5 + 0.2; d2 = dx * dx + dy * dy; }
      const w = (repel * alpha) / d2;
      p.vx -= dx * w; p.vy -= dy * w; q.vx += dx * w; q.vy += dy * w;
    }
  }
  for (const l of links) {
    const p = nodes[l.a]; const q = nodes[l.b];
    const dx = q.x + q.vx - p.x - p.vx; const dy = q.y + q.vy - p.y - p.vy;
    const d = Math.sqrt(dx * dx + dy * dy) || 1;
    const k = ((d - l.len) / d) * alpha * l.k * 0.5 * f.link;
    p.vx += dx * k; p.vy += dy * k; q.vx -= dx * k; q.vy -= dy * k;
  }
  const c = 0.012 * f.center * alpha;
  let moved = 0;
  for (const p of nodes) {
    if (p.fixed) { p.vx = 0; p.vy = 0; continue; }
    p.vx -= p.x * c; p.vy -= p.y * c;
    p.vx *= 0.6; p.vy *= 0.6;
    const vmax = 60;
    const v = Math.hypot(p.vx, p.vy);
    if (v > vmax) { p.vx *= vmax / v; p.vy *= vmax / v; }
    p.x += p.vx; p.y += p.vy;
    moved = Math.max(moved, Math.abs(p.vx) + Math.abs(p.vy));
  }
  collide(nodes);
  return moved;
}

/** Circles never overlap: pairs that touch are pushed apart (a fixed one does not move). */
export function collide(nodes, pad = 8, passes = 2) {
  const n = nodes.length;
  for (let pass = 0; pass < passes; pass++) {
    for (let i = 0; i < n; i++) {
      const p = nodes[i];
      if (p.ghost) continue;
      for (let j = i + 1; j < n; j++) {
        const q = nodes[j];
        if (q.ghost) continue;
        const min = p.r + q.r + pad;
        let dx = q.x - p.x; let dy = q.y - p.y;
        if (Math.abs(dx) > min || Math.abs(dy) > min) continue;
        let d = Math.sqrt(dx * dx + dy * dy);
        if (d >= min) continue;
        if (d < 0.01) { dx = 0.01 * (j - i); dy = 0.013; d = Math.hypot(dx, dy); }
        const push = (min - d) / d;
        if (p.fixed && q.fixed) continue;
        if (p.fixed) { q.x += dx * push; q.y += dy * push; }
        else if (q.fixed) { p.x -= dx * push; p.y -= dy * push; }
        else { p.x -= dx * push * 0.5; p.y -= dy * push * 0.5; q.x += dx * push * 0.5; q.y += dy * push * 0.5; }
      }
    }
  }
}

/** Run the simulation to rest (for a fresh map, the file export and Node). */
export function settle(nodes, links, { ticks = 300, alpha = 1, f = FORCE_DEFAULTS } = {}) {
  let a = alpha;
  for (let t = 0; t < ticks && a > 0.004; t++) { step(nodes, links, a, f); a += (0 - a) * 0.0228; }
  for (let i = 0; i < 6; i++) collide(nodes, 8, 2);
  return nodes;
}

/** The next alpha: the simulation cools a little every tick, like d3-force. */
export const cool = (a) => a + (0 - a) * 0.0228;
export const ALPHA_MIN = 0.006;
